import type {
  VmetaImportProblem,
  VmetaImportReasonTotal,
  VmetaImportRunSummary,
} from "common";

/** Platforms whose copied report may name records. Any other platform gets counts only. */
export const COPY_IDENTIFIER_PLATFORMS = ["freezerpro"] as const;

export const FIXER_HEADING = {
  data: "For the data entry team",
  setup: "For the administrator",
} as const;

const identifiersAllowed = (platform: string | null): boolean =>
  (COPY_IDENTIFIER_PLATFORMS as readonly string[]).includes(platform ?? "");

/** One problem as a line with identifiers, for allowlisted platforms only. */
export const problemLine = (p: VmetaImportProblem): string => {
  if (p.step === "parent-update" && p.identifier) {
    const where = [p.collection, p.field].filter(Boolean).join(".");
    const children = p.children?.length
      ? `, from ${p.children.join(", ")}${p.childrenTruncated ? ` and ${p.childrenTruncated} more` : ""}`
      : "";
    return `${p.identifier} (${where})${children}: ${p.reason}`;
  }
  if (p.step === "parent-ref" && p.identifier) {
    const where = [p.collection, p.field].filter(Boolean).join(".");
    const inner = [where, p.reference && `parent reference ${p.reference}`]
      .filter(Boolean)
      .join(", ");
    return `${p.identifier}${inner ? ` (${inner})` : ""}: ${p.reason}`;
  }
  return p.identifier ? `${p.identifier}: ${p.reason}` : p.reason;
};

/** One reason total as a line with counts only. A run problem has no record. */
const totalLine = (t: VmetaImportReasonTotal): string =>
  t.records > 0
    ? `${t.problems} ${t.problems === 1 ? "problem" : "problems"} in ${t.records} ${t.records === 1 ? "record" : "records"}: ${t.reason}`
    : t.reason;

export const buildRunReport = (args: {
  configName: string;
  projectName: string;
  platform: string | null;
  run: VmetaImportRunSummary;
  problems: VmetaImportProblem[];
  problemsTruncated: number;
  totals: VmetaImportReasonTotal[];
}): string => {
  const { run, problems, totals } = args;
  const lines: string[] = [`Data import: ${args.configName}`];
  if (args.projectName) lines.push(`Project: ${args.projectName}`);
  if (run.message) lines.push(run.message);
  if (run.dateEnded) lines.push(`Finished: ${run.dateEnded}`);
  const withIds = identifiersAllowed(args.platform);
  for (const fixer of ["data", "setup"] as const) {
    const section = withIds
      ? problems.filter(p => p.fixer === fixer).map(problemLine)
      : totals.filter(t => t.fixer === fixer).map(totalLine);
    if (section.length === 0) continue;
    lines.push("", FIXER_HEADING[fixer], ...section.map(l => `- ${l}`));
  }
  if (withIds && args.problemsTruncated > 0)
    lines.push("", `and ${args.problemsTruncated} more not shown`);
  return lines.join("\n");
};
